import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/NumberField";
import { Trash2, Plus, ChevronDown, Minus, Sparkles, Layers } from "lucide-react";

/**
 * Mobile-first quote items editor.
 *
 * Renders a vertical card-stack instead of the desktop grid-12 table.
 * Each card lays out:
 *   - Header: description input (full-width) + delete icon
 *   - Body: 2-column grid (Qty stepper, Discount %), full-width Unit price (€ prefix)
 *   - Footer: line total (champagne) — strike-through original when discounted
 *   - Toggle row: "Servizio opzionale" iOS-style switch
 *
 * Compact mode:
 *   - Activates automatically when items >= COMPACT_THRESHOLD (6 items)
 *     OR when the user toggles the global "Modalità compatta" pill at the top.
 *   - Compact card shows only [desc] [line total] [chevron].
 *   - Tap on the card → expands to reveal full edit body. Tap again or open
 *     another card → previous collapses (accordion-like, only one open at a
 *     time when compact, to keep the screen tidy).
 *
 * Every interactive control has a data-testid for the testing agent.
 * Mobile-only — desktop keeps the existing grid-12 table untouched.
 */
const fmtEuro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(parseFloat(n) || 0);

const COMPACT_THRESHOLD = 6;

export function QuoteItemsMobile({
  items,
  serviceOptions,
  customSentinel,
  onAdd,
  onUpdate,
  onRemove,
}) {
  const [autoCompact, setAutoCompact] = useState(items.length >= COMPACT_THRESHOLD);
  const [openIndex, setOpenIndex] = useState(null);

  // Auto-enable compact when crossing the threshold. We don't auto-disable
  // when items go below the threshold — once the operator has experienced
  // compact mode for a long quote, switching back unexpectedly would feel
  // jarring. They can opt out via the toggle.
  useEffect(() => {
    if (items.length >= COMPACT_THRESHOLD) setAutoCompact(true);
  }, [items.length]);

  const compact = autoCompact;
  // When NOT compact, all cards are expanded. When compact, only the
  // `openIndex` card body is visible.
  const isCardOpen = (i) => !compact || openIndex === i;

  const toggleCard = (i) => {
    if (!compact) return;
    setOpenIndex((cur) => (cur === i ? null : i));
  };

  return (
    <div className="md:hidden" data-testid="quote-items-mobile">
      {/* Compact-mode toggle pill */}
      <div className="flex items-center justify-between mb-3 px-0.5">
        <span className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 font-medium">
          Servizi · {items.length} {items.length === 1 ? "voce" : "voci"}
        </span>
        {items.length >= 3 && (
          <button
            type="button"
            onClick={() => {
              const next = !autoCompact;
              setAutoCompact(next);
              if (!next) setOpenIndex(null);
            }}
            className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
              autoCompact
                ? "bg-primary/12 text-primary border-primary/30"
                : "bg-white text-neutral-600 border-neutral-200"
            }`}
            data-testid="quote-mobile-compact-toggle"
          >
            <Layers className="w-3 h-3" strokeWidth={1.6} />
            {autoCompact ? "Compatto" : "Espanso"}
          </button>
        )}
      </div>

      <div className="space-y-3">
        {items.map((l, i) => (
          <QuoteCard
            key={i}
            index={i}
            line={l}
            open={isCardOpen(i)}
            compact={compact}
            onToggle={() => toggleCard(i)}
            onUpdate={(k, v) => onUpdate(i, k, v)}
            onRemove={() => onRemove(i)}
            serviceOptions={serviceOptions}
            customSentinel={customSentinel}
          />
        ))}
      </div>

      {/* Add service CTA (dashed champagne button) */}
      <button
        type="button"
        onClick={onAdd}
        className="mt-4 w-full h-[52px] rounded-xl border-[1.5px] border-dashed border-primary/40 bg-primary/[0.04] text-primary font-medium text-[15px] inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        data-testid="quote-mobile-add-item"
      >
        <Plus className="w-[18px] h-[18px]" strokeWidth={1.8} />
        Aggiungi servizio
      </button>
    </div>
  );
}

function QuoteCard({
  index,
  line,
  open,
  compact,
  onToggle,
  onUpdate,
  onRemove,
  serviceOptions,
  customSentinel,
}) {
  const isOptional = !!line.is_optional;
  const decision = isOptional
    ? line.client_decision || (line.client_selected ? "accepted" : "pending")
    : "accepted";
  const isAccepted = isOptional && decision === "accepted";
  const isDeclined = isOptional && decision === "declined";
  const isPending = isOptional && decision === "pending";

  const qty = parseFloat(line.qty) || 0;
  const price = parseFloat(line.price) || 0;
  const discount = parseFloat(line.discount) || 0;
  const gross = qty * price;
  const lineTotal = gross * (1 - discount / 100);

  const optional = isOptional;
  const cardClass = optional
    ? "bg-[#FFF8EC] border border-[#E8D9B7] relative"
    : "bg-white border border-neutral-200";

  return (
    <div
      className={`${cardClass} rounded-xl overflow-hidden`}
      data-testid={`quote-mobile-item-${index}`}
    >
      {optional && (
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-1 bg-primary"
        />
      )}

      {/* HEAD: descrizione + delete */}
      <div
        className={`flex items-start gap-2 px-3.5 pt-3.5 ${
          compact && !open ? "pb-3" : "pb-2.5 border-b border-neutral-100"
        }`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 font-medium">
              Descrizione
            </span>
            {optional && (
              <span
                className="inline-flex items-center gap-1 text-[9px] tracking-[0.16em] uppercase text-primary bg-primary/15 px-1.5 py-0.5 rounded font-semibold"
                data-testid={`quote-mobile-badge-optional-${index}`}
              >
                Opzionale
              </span>
            )}
            {isAccepted && (
              <span className="inline-flex items-center text-[9px] tracking-[0.16em] uppercase text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded font-semibold">
                Confermato
              </span>
            )}
            {isDeclined && (
              <span className="inline-flex items-center text-[9px] tracking-[0.16em] uppercase text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded font-semibold">
                Declinato
              </span>
            )}
          </div>

          {compact && !open ? (
            // Compact summary line — tap to expand
            <button
              type="button"
              onClick={onToggle}
              className="w-full text-left flex items-center justify-between gap-3"
              data-testid={`quote-mobile-card-expand-${index}`}
            >
              <span
                className={`flex-1 text-[15px] font-medium leading-snug break-words ${
                  optional ? "italic text-[#7B6A45]" : "text-neutral-900"
                } ${isDeclined ? "line-through text-neutral-400" : ""}`}
              >
                {line.desc || <span className="text-neutral-400 font-normal">Nuovo servizio</span>}
              </span>
              <span className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`tabular-nums text-[15px] font-semibold ${
                    optional || isPending ? "text-neutral-500" : "text-[#7B6A45]"
                  }`}
                  data-testid={`quote-mobile-line-total-collapsed-${index}`}
                >
                  {fmtEuro(lineTotal)}
                </span>
                <ChevronDown className="w-4 h-4 text-neutral-400" strokeWidth={1.8} />
              </span>
            </button>
          ) : (
            <Input
              list={`quote-service-options-mobile-${index}`}
              className={`h-11 text-[16px] rounded-lg break-words ${
                optional ? "italic text-[#7B6A45]" : ""
              } ${isDeclined ? "line-through text-neutral-400" : ""}`}
              placeholder="Es. Trucco sposa"
              value={line.desc || ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v === customSentinel) {
                  onUpdate("desc", "");
                } else {
                  onUpdate("desc", v);
                }
              }}
              data-testid={`quote-mobile-desc-${index}`}
            />
          )}
          <datalist id={`quote-service-options-mobile-${index}`}>
            {(serviceOptions || []).map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        </div>

        {/* Delete icon - hidden in compact-collapsed state to declutter */}
        {(!compact || open) && (
          <button
            type="button"
            onClick={onRemove}
            className="w-10 h-10 -mr-1 -mt-1 inline-flex items-center justify-center rounded-lg text-neutral-400 active:bg-neutral-100"
            aria-label="Elimina riga"
            data-testid={`quote-mobile-remove-${index}`}
          >
            <Trash2 className="w-[18px] h-[18px]" strokeWidth={1.6} />
          </button>
        )}
      </div>

      {/* BODY + FOOT + TOGGLE: only when card is open */}
      {open && (
        <>
          <div className="px-3.5 py-3 grid grid-cols-2 gap-x-3 gap-y-3">
            {/* Quantità stepper */}
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 font-medium mb-1.5">
                Quantità
              </div>
              <QtyStepper
                value={line.qty ?? 1}
                onChange={(n) => onUpdate("qty", n)}
                testIdBase={`quote-mobile-qty-${index}`}
              />
            </div>

            {/* Sconto % */}
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 font-medium mb-1.5">
                Sconto
              </div>
              <div className="flex items-center h-11 rounded-lg border border-neutral-200 bg-white px-3">
                <NumberField
                  className="flex-1 min-w-0 border-0 px-0 h-11 text-[16px] text-right tabular-nums focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
                  value={line.discount ?? 0}
                  onChange={(n) => onUpdate("discount", n)}
                  min={0}
                  max={100}
                  allowDecimals={false}
                  data-testid={`quote-mobile-discount-${index}`}
                />
                <span className="text-[15px] text-neutral-500 pl-1.5 flex-shrink-0">%</span>
              </div>
            </div>

            {/* Prezzo unitario */}
            <div className="col-span-2">
              <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 font-medium mb-1.5">
                Prezzo unitario
              </div>
              <div className="flex items-center h-11 rounded-lg border border-neutral-200 bg-white px-3">
                <span className="text-[15px] text-neutral-500 pr-1.5 flex-shrink-0">€</span>
                <NumberField
                  className="flex-1 min-w-0 border-0 px-0 h-11 text-[16px] text-right tabular-nums focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
                  value={line.price ?? 0}
                  onChange={(n) => onUpdate("price", n)}
                  min={0}
                  data-testid={`quote-mobile-price-${index}`}
                />
              </div>
            </div>
          </div>

          {/* FOOT: line total */}
          <div className="px-3.5 py-3 border-t border-neutral-100 flex items-center justify-between gap-3">
            <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 font-medium">
              {optional ? "Totale riga · non in totale" : "Totale riga"}
            </div>
            <div className="text-right">
              {discount > 0 && (
                <div
                  className="text-[11px] text-neutral-400 line-through tabular-nums leading-none mb-0.5"
                  data-testid={`quote-mobile-gross-${index}`}
                >
                  {fmtEuro(gross)}
                </div>
              )}
              <div
                className={`text-[20px] font-semibold tabular-nums leading-none ${
                  optional || isPending ? "text-neutral-500" : "text-[#7B6A45]"
                } ${isDeclined ? "line-through" : ""}`}
                data-testid={`quote-mobile-line-total-${index}`}
              >
                {fmtEuro(lineTotal)}
              </div>
            </div>
          </div>

          {/* OPTIONAL toggle (iOS-style switch) */}
          <div className="px-3.5 py-3 border-t border-neutral-100 bg-neutral-50/60 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[14px] text-neutral-700 leading-tight">Servizio opzionale</div>
              <div className="text-[11px] text-neutral-500 mt-0.5 leading-tight">
                Mostrato alla sposa come "su richiesta"
              </div>
            </div>
            <IOSSwitch
              on={isOptional}
              onToggle={() => onUpdate("is_optional", !isOptional)}
              testId={`quote-mobile-optional-${index}`}
            />
          </div>

          {/* Tap-to-collapse handle (compact mode only) */}
          {compact && (
            <button
              type="button"
              onClick={onToggle}
              className="w-full py-2 text-[11px] text-neutral-500 active:bg-neutral-100"
              data-testid={`quote-mobile-card-collapse-${index}`}
            >
              Comprimi
            </button>
          )}
        </>
      )}
    </div>
  );
}

function QtyStepper({ value, onChange, testIdBase }) {
  const cur = parseFloat(value) || 0;
  const dec = () => onChange(Math.max(0, Math.round(cur) - 1));
  const inc = () => onChange(Math.round(cur) + 1);
  return (
    <div className="flex items-stretch h-11 rounded-lg border border-neutral-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={dec}
        className="w-11 flex items-center justify-center text-neutral-500 active:bg-primary/10"
        aria-label="Diminuisci quantità"
        data-testid={`${testIdBase}-minus`}
      >
        <Minus className="w-4 h-4" strokeWidth={1.8} />
      </button>
      <NumberField
        className="flex-1 min-w-0 h-11 border-0 text-center text-[17px] tabular-nums focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none rounded-none px-0"
        value={value}
        onChange={(n) => onChange(n)}
        allowDecimals={false}
        min={0}
        data-testid={testIdBase}
      />
      <button
        type="button"
        onClick={inc}
        className="w-11 flex items-center justify-center text-neutral-500 active:bg-primary/10"
        aria-label="Aumenta quantità"
        data-testid={`${testIdBase}-plus`}
      >
        <Plus className="w-4 h-4" strokeWidth={1.8} />
      </button>
    </div>
  );
}

function IOSSwitch({ on, onToggle, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
        on ? "bg-primary" : "bg-neutral-300"
      }`}
      data-testid={testId}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
          on ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

/**
 * Totale banner — sticky-friendly visual summary shown at the top of the
 * mobile quote tab. Reads from the recalculated quote object so it stays
 * in sync with edits without extra plumbing.
 */
export function QuoteTotalBanner({ quote }) {
  const items = quote?.items || [];
  const includedCount = items.filter(
    (l) => !l.is_optional || l.client_decision === "accepted" || l.client_selected === true,
  ).length;
  const optionalCount = items.filter((l) => !!l.is_optional).length;
  const discountCount = items.filter((l) => (parseFloat(l.discount) || 0) > 0).length;
  const taxMode = quote?.tax_mode || (Number(quote?.tax) === 0 ? "forfettario" : "standard");
  const taxLabel =
    taxMode === "forfettario" ? "Regime forfettario" : `IVA ${Number(quote?.tax ?? 22)}% inclusa`;

  return (
    <div
      className="rounded-2xl px-5 py-4 text-white shadow-[0_6px_20px_rgba(200,169,126,0.32)] flex items-center justify-between"
      style={{
        background: "linear-gradient(135deg, hsl(var(--primary)) 0%, #b89868 100%)",
      }}
      data-testid="quote-mobile-total-banner"
    >
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.2em] uppercase opacity-85 font-medium">
          Totale preventivo
        </div>
        <div
          className="font-serif-heading font-semibold leading-none mt-1 tabular-nums break-all"
          style={{ fontSize: "30px" }}
          data-testid="quote-mobile-total"
        >
          {fmtEuro(quote?.total)}
        </div>
        <div className="text-[11px] opacity-85 mt-1.5">
          {includedCount} {includedCount === 1 ? "servizio" : "servizi"}
          {optionalCount > 0 && ` · ${optionalCount} opz.`}
          {discountCount > 0 && ` · ${discountCount} sconti`}
          {" · "}
          {taxLabel}
        </div>
      </div>
      <Sparkles className="w-7 h-7 opacity-70 flex-shrink-0" strokeWidth={1.5} />
    </div>
  );
}

export default QuoteItemsMobile;
